import dayjs from '~/timeInit';

import type { CppInvoiceTransaction } from '~/services/LuxLoyaltyService.types';

import { currencyCode, formatBandRates } from './invoiceFormat';

/** One CSV row, keyed by {@link transactionCsvHeaders}. */
export type TransactionCsvRow = Record<string, string | number>;

/**
 * Renders the CPP rate(s) a transaction was priced at.
 *
 * @returns The rate(s), e.g. `"1.2c"` or `"1.2c, 0.9c"`; empty when the row could not be priced.
 */
export function formatTransactionCpp(transaction: CppInvoiceTransaction): string {
  return formatBandRates(transaction.bands);
}

/** Formats a signed cents amount as a bare decimal for CSV, blank when unpriced. */
function csvAmount(amountCents: number | null): string {
  return amountCents === null ? '' : (amountCents / 100).toFixed(2);
}

/**
 * Converts a page (or the whole period) of transactions into CSV rows.
 *
 * The partner name is passed in rather than read off the row: the endpoint is already scoped to one
 * partner, so it does not repeat the name per transaction.
 *
 * @param transactions - The transactions to export.
 * @param partnerName - The partner the export is scoped to.
 * @param currency - The period's currency, used only for the cost column heading.
 */
export function transactionsToCsvRows(
  transactions: Array<CppInvoiceTransaction>,
  partnerName: string,
  currency: string | null,
): Array<TransactionCsvRow> {
  const costHeader = `Cost (${currencyCode(currency)})`;

  return transactions.map((transaction) => ({
    'Transaction ID': transaction.transactionId,
    Date: transaction.date,
    // `date` is the service's own day in the program timezone. Rendering the time beside it in UTC
    // put the two a calendar day apart for anything after 10am Sydney — on the file a partner
    // reconciles a disputed invoice against. Both columns are now the same clock.
    Time: dayjs(transaction.createdAt).tz().format('HH:mm:ss'),
    Partner: partnerName,
    Type: transaction.transactionType,
    Activity: transaction.activityTitle,
    Source: transaction.source,
    'Account Number': transaction.accountNumber,
    Points: transaction.signedPoints,
    'Status Credits': transaction.statusCredits,
    CPP: formatTransactionCpp(transaction),
    'Reverses Transaction ID': transaction.originalTransactionId ?? '',
    [costHeader]: csvAmount(transaction.amountCents),
  }));
}

/**
 * Column order of the exported activity statement, with the cost column named for the period's
 * currency.
 *
 * This CSV is the backup attachment that accompanies a partner invoice, so it deliberately carries
 * more than the on-screen grid: the ledger ids and the customer/account the points landed on are
 * what a partner dispute is actually reconciled with.
 *
 * It deliberately carries LESS in one respect: how a row was priced (its rate card and the day it
 * was priced against) and why a row carries no cost (its cost status, exclusion reason and cost
 * source) are internal pricing mechanics, and stay in the admin grid rather than going out to a
 * partner. `CPP` and a blank `Cost` are all the statement itself needs to show.
 *
 * The single source of column order — {@link transactionsToCsvRows} builds its rows to match.
 *
 * @param currency - The period's currency.
 */
export function transactionCsvHeaders(currency: string | null): Array<string> {
  return [
    'Transaction ID',
    'Date',
    'Time',
    'Partner',
    'Type',
    'Activity',
    'Source',
    'Account Number',
    'Points',
    'Status Credits',
    'CPP',
    'Reverses Transaction ID',
    `Cost (${currencyCode(currency)})`,
  ];
}
